import React, { useMemo, useState } from "react"
import { Autocomplete, Box, TextField, Typography } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"
import AppBrandMark from "@components/DataBlocks/BrandMark"
import AppIdentityChip from "./IdentityChip"
import { addId, makePrimary, removeId } from "./@services/identity"


export const fieldLabelSx = { display: 'block', fontSize: 13, fontWeight: 600, color: dsColorNew.textDark, mb: 0.75 }
export const fieldNoteSx = { fontSize: 12, fontWeight: 500, color: dsColorNew.textSubtle }
export const helperSx = { fontSize: 12, color: dsColorNew.textMuted, mt: 0.75, lineHeight: 1.45 }
export const boxSx = (error) => ({
	display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.25, borderRadius: '10px',
	border: `1px solid ${error ? dsColorNew.dangerRed : dsColorNew.grayBorder}`,
	backgroundColor: error ? dsColorNew.dangerBg : dsColorNew.white,
})
export const chipsSx = { display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }

/** Picks one option, adds it and empties itself: the chips below hold the list in its order. */
export function AddPicker({ options, selected, placeholder, renderLabel, disabled, emptyText, ...props }) {
	const [input, setInput] = useState('')

	return <Autocomplete
		size="small"
		value={null}
		inputValue={input}
		onInputChange={(_, v, reason) => setInput(reason === 'reset' ? '' : v)}
		options={options}
		disabled={disabled}
		blurOnSelect
		clearOnBlur
		isOptionEqualToValue={(o, v) => o.value === v?.value}
		getOptionLabel={o => o?.label ?? ''}
		getOptionDisabled={o => selected.includes(o.value)}
		noOptionsText={emptyText}
		renderOption={(liProps, o) => <Box component="li" {...liProps} key={o.value} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<Box component="span" sx={{ width: 14, display: 'inline-flex', color: dsColorNew.appBlue }}>
				{selected.includes(o.value) && <IconSeeker icon="check" sx={{ fontSize: 11 }}/>}
			</Box>
			{renderLabel(o)}
			{selected.includes(o.value) && <Typography component="span" sx={{ ml: 'auto', fontSize: 11, color: dsColorNew.textSubtle }}>added</Typography>}
		</Box>}
		renderInput={params => <TextField {...params} placeholder={placeholder} inputProps={{ ...params.inputProps, 'aria-label': placeholder }}/>}
		sx={{ maxWidth: 420, '& .MuiOutlinedInput-root': { backgroundColor: dsColorNew.white } }}
		{...props}
	/>
}

/** Brands: the user's providers by name, the first being "you" (criterion 3, 5). */
export default function BrandsField({ value, onChange, labels, options, error, disabled }) {
	const catalogue = useMemo(() => [...(options || [])]
		.filter(o => o.label)
		.sort((a, b) => String(a.label).localeCompare(String(b.label))), [options])

	return <Box sx={{ gridColumn: 'span 12' }}>
		<Typography component="label" sx={fieldLabelSx}>
			Brands <Box component="span" sx={fieldNoteSx}>· {value.length} · at least one</Box>
		</Typography>
		<Box sx={boxSx(!!error)}>
			{!!value.length && <Box sx={chipsSx}>
				{value.map((id, i) => <AppIdentityChip
					key={id}
					id={id}
					kind="brand"
					label={labels.providers.get(id) ?? null}
					primary={i === 0}
					disabled={disabled}
					onMakePrimary={() => onChange(makePrimary(value, id))}
					onRemove={() => onChange(removeId(value, id))}
				/>)}
			</Box>}
			<AddPicker
				options={catalogue}
				selected={value}
				disabled={disabled}
				placeholder="Add a brand — type its name…"
				emptyText="No brands to offer: your account has no captured providers yet"
				renderLabel={o => <><AppBrandMark name={o.label}/>{o.label}</>}
				onChange={(_, o) => o && onChange(addId(value, o.value))}
			/>
		</Box>
		{error
			? <Typography role="alert" sx={{ ...helperSx, color: dsColorNew.dangerRed }}>{error}</Typography>
			: <Typography sx={helperSx}>
				The <b>first brand is “you”</b>.{value.length > 1 && <> The others are also yours. <IconSeeker icon="star" iconWeight="regular" sx={{ fontSize: 10 }}/> makes one the first.</>}
			</Typography>}
	</Box>
}
